/**
 * Role-based permission hook for the 5-role RBAC model (A2F-434).
 *
 * Hierarchy: admin (4) > lead (3) > contributor (2) > viewer (1) > author (0).
 * Author is for ribbon-only users — valid but below viewer.
 * When auth is disabled (local dev), user is null — all checks return true
 * since the backend grants synthetic admin identity.
 */

import { useAuth } from './AuthContext';

const ROLE_LEVELS: Record<string, number> = {
  author: 0,
  viewer: 1,
  contributor: 2,
  lead: 3,
  admin: 4,
};

export function usePermissions() {
  const { user, authRequired } = useAuth();

  // When auth is disabled, everything is permitted (backend runs as admin)
  if (!authRequired) {
    return {
      role: 'admin' as const,
      canRunScans: true,
      canDismissFindings: true,
      canSignOff: true,
      canDeclareReadiness: true,
      canCompleteSubmission: true,
      canViewAudit: true,
      canGenerateReport: true,
      canCreateProjects: true,
      canDeleteProjects: true,
      canReadUsers: true,
      canManageUsers: true,
      canReadSettings: true,
      canManageSettings: true,
      hasRole: (_minRole: string) => true,
    };
  }

  const role = user?.role || 'viewer';
  const level = ROLE_LEVELS[role] || 0;

  return {
    role,
    canRunScans: level >= 2,          // contributor+
    canDismissFindings: level >= 2,    // contributor+
    canSignOff: level >= 2,            // contributor+
    canDeclareReadiness: level >= 3,   // lead+
    canCompleteSubmission: level >= 3, // lead+
    canViewAudit: level >= 2,          // contributor+
    canGenerateReport: level >= 3,     // lead+
    canCreateProjects: level >= 3,     // lead+
    canDeleteProjects: level >= 3,     // lead+
    canReadUsers: level >= 3,          // lead+
    canManageUsers: level >= 4,        // admin
    canReadSettings: level >= 3,       // lead+
    canManageSettings: level >= 4,     // admin
    hasRole: (minRole: string) => level >= (ROLE_LEVELS[minRole] || 0),
  };
}
